import WebsiteRepositoryInterface, {
  Website,
  WebsiteComposite,
} from '@/services/Website/interfaces';
import { DnsCheckerRepositoryInterface, DnsCheckResult } from '@/services/DnsChecker';
import { CertificatesRepositoryInterface, CertificateInterface } from '@/services/SSL/interfaces';
import WebsiteLogRepositoryInterface from '@/services/WebsiteLog/interfaces';
import {
  ConsoleActionExecutorDeps,
  openConsoleAction,
} from './actionExecutor';
import { getHelpCommands } from './commands';
import {
  contextFromWebsite,
  loadCommandHistory,
  pushRecentTarget,
} from './history';
import { isNumericId, requireTarget, sitesFiltersToLabel } from './parser';
import {
  ConsoleExecuteOutcome,
  ConsoleResult,
  ConsoleSiteContext,
  ParsedConsoleCommand,
  SitesFilters,
} from './types';

export interface ConsoleExecutorDeps {
  websiteRepository: WebsiteRepositoryInterface;
  dnsCheckerRepository: DnsCheckerRepositoryInterface;
  certificateRepository: CertificatesRepositoryInterface;
  websiteLogRepository: WebsiteLogRepositoryInterface;
  getUserId: () => string;
  context: ConsoleSiteContext | null;
  /** Required for /actions and mutation command panels. */
  actionDeps?: ConsoleActionExecutorDeps;
}

function apiErrorMessage(error: any, fallback: string): string {
  if (error && error.response) {
    const status = error.response.status;
    const data = error.response.data;
    const msg = data && (data.message || data.error || data.Error);
    if (status === 404) {
      return '';
    }
    if (msg) {
      return String(msg);
    }
    if (status === 403) {
      return 'Permission denied.';
    }
    if (status >= 500) {
      return `Server error (${status}).`;
    }
    return `Request failed (${status}).`;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
}

function isNotFound(error: any): boolean {
  return !!(error && error.response && error.response.status === 404);
}

async function searchByDomain(
  repository: WebsiteRepositoryInterface,
  domain: string,
  page = 1,
  pageSize = 25,
): Promise<{ websites: Website[]; count: number }> {
  const response = await repository.searchWebsites(page, domain, undefined, undefined, undefined, pageSize);
  return {
    websites: response.Websites || [],
    count: response.count || 0,
  };
}

async function resolveWebsiteChoices(
  repository: WebsiteRepositoryInterface,
  query: string,
): Promise<{ exact?: WebsiteComposite; websites: Website[]; count: number; error?: string }> {
  if (isNumericId(query)) {
    try {
      const composite = await repository.getWebsiteById(parseInt(query, 10));
      return { exact: composite, websites: [composite.website], count: 1 };
    } catch (error) {
      if (isNotFound(error)) {
        return { websites: [], count: 0 };
      }
      return { websites: [], count: 0, error: apiErrorMessage(error, 'Failed to load website.') };
    }
  }

  try {
    const { websites, count } = await searchByDomain(repository, query, 1, 25);
    const q = query.toLowerCase();
    const exactMatches = websites.filter((site) => site.domain.toLowerCase() === q);

    if (exactMatches.length === 1) {
      try {
        const composite = await repository.getWebsiteById(exactMatches[0].id);
        return { exact: composite, websites: [composite.website], count: 1 };
      } catch (error) {
        if (isNotFound(error)) {
          return { websites: exactMatches, count: 1 };
        }
        return { websites: [], count: 0, error: apiErrorMessage(error, 'Failed to load website.') };
      }
    }

    if (exactMatches.length > 1) {
      return { websites: exactMatches, count: exactMatches.length };
    }

    return { websites, count };
  } catch (error) {
    return { websites: [], count: 0, error: apiErrorMessage(error, 'Search failed.') };
  }
}

function rememberResolved(website: Website): ConsoleSiteContext {
  return contextFromWebsite(website);
}

async function loadSsl(
  deps: ConsoleExecutorDeps,
  composite: WebsiteComposite,
): Promise<{ certificate?: CertificateInterface; error?: string }> {
  const certId = composite.details && composite.details.sslCertificateId;
  try {
    if (certId) {
      const certificate = await deps.certificateRepository.getCertificateById(certId);
      return { certificate };
    }
    const certificate = await deps.certificateRepository.getCertificate(composite.website.domain);
    return { certificate };
  } catch (error) {
    if (isNotFound(error)) {
      return {};
    }
    return { error: apiErrorMessage(error, 'Failed to load SSL certificate.') };
  }
}

async function loadDns(
  deps: ConsoleExecutorDeps,
  domain: string,
): Promise<{ dns?: DnsCheckResult; error?: string }> {
  try {
    const dns = await deps.dnsCheckerRepository.checkDomain(domain);
    return { dns };
  } catch (error) {
    return { error: apiErrorMessage(error, 'DNS check failed.') };
  }
}

function choicesOutcome(
  websites: Website[],
  count: number,
  query: string,
  intendedCommand: string,
  label: string,
): ConsoleExecuteOutcome {
  const statuses = Array.from(new Set(websites.map((site) => site.status).filter(Boolean)));
  const statusHint = statuses.length > 1
    ? ` (${statuses.join(' / ')})`
    : '';
  return {
    result: {
      kind: 'choices',
      websites,
      count,
      message: `Multiple websites matched "${query}"${statusHint}. Pick one by ID/server${label}:`,
      intendedCommand,
    },
  };
}

function openNavigateOutcome(
  composite: WebsiteComposite,
  target: 'website' | 'server' | 'group' | 'unix' | undefined,
  ctx: ConsoleSiteContext,
): ConsoleExecuteOutcome {
  const website = composite.website;

  if (!target || target === 'website') {
    return {
      result: {
        kind: 'text',
        message: `Opening ${website.domain} (#${website.id})…`,
      },
      navigateToWebsiteId: website.id,
      setContext: ctx,
    };
  }

  if (target === 'server') {
    if (!website.originServer) {
      return {
        result: { kind: 'error', message: `Website ${website.domain} has no origin server.` },
        setContext: ctx,
      };
    }
    return {
      result: {
        kind: 'text',
        message: `Opening server ${website.originServer}…`,
      },
      navigateTo: {
        name: 'server_view',
        params: { server: website.originServer },
      },
      setContext: ctx,
    };
  }

  if (target === 'group') {
    if (!composite.group) {
      return {
        result: { kind: 'error', message: `Website ${website.domain} has no group.` },
        setContext: ctx,
      };
    }
    return {
      result: {
        kind: 'text',
        message: `Opening group #${composite.group.id}…`,
      },
      navigateTo: {
        name: 'website_group_view',
        params: { id: String(composite.group.id) },
      },
      setContext: ctx,
    };
  }

  // unix
  const unixUserId = composite.details && composite.details.unixUserId;
  if (!unixUserId) {
    return {
      result: { kind: 'error', message: `Website ${website.domain} has no unix user.` },
      setContext: ctx,
    };
  }
  return {
    result: {
      kind: 'text',
      message: `Opening unix user #${unixUserId}…`,
    },
    navigateTo: {
      name: 'system_user_edit',
      params: { systemUserId: String(unixUserId) },
    },
    setContext: ctx,
  };
}

export async function executeConsoleCommand(
  parsed: ParsedConsoleCommand,
  deps: ConsoleExecutorDeps,
): Promise<ConsoleExecuteOutcome> {
  const { websiteRepository } = deps;

  switch (parsed.kind) {
    case 'error':
      return { result: { kind: 'error', message: parsed.message } };

    case 'help':
      return { result: { kind: 'help', commands: getHelpCommands() } };

    case 'clear':
      return {
        result: { kind: 'text', message: 'Transcript cleared.' },
        clearTranscript: true,
      };

    case 'history': {
      const entries = loadCommandHistory();
      if (!entries.length) {
        return { result: { kind: 'empty', message: 'No command history yet.' } };
      }
      return { result: { kind: 'history', entries } };
    }

    case 'rerun':
      return { result: { kind: 'error', message: 'No previous command to re-run.' } };

    case 'find': {
      if (isNumericId(parsed.query)) {
        try {
          const composite = await websiteRepository.getWebsiteById(parseInt(parsed.query, 10));
          const ctx = rememberResolved(composite.website);
          return {
            result: { kind: 'detail', composite },
            setContext: ctx,
          };
        } catch (error) {
          if (isNotFound(error)) {
            return {
              result: { kind: 'empty', message: `No website found with ID ${parsed.query}.` },
            };
          }
          return {
            result: { kind: 'error', message: apiErrorMessage(error, 'Failed to load website.') },
          };
        }
      }

      try {
        const { websites, count } = await searchByDomain(websiteRepository, parsed.query, 1, 25);
        if (!websites.length) {
          return {
            result: {
              kind: 'empty',
              message: `No websites matched "${parsed.query}".`,
            },
          };
        }

        if (websites.length === 1) {
          rememberResolved(websites[0]);
        } else {
          pushRecentTarget(parsed.query);
        }

        const filters: SitesFilters = {
          page: 1,
          limit: 25,
          domain: parsed.query,
        };

        return {
          result: {
            kind: 'search',
            websites,
            count,
            page: 1,
            pageSize: 25,
            filters,
            queryLabel: sitesFiltersToLabel(filters),
          },
        };
      } catch (error) {
        return {
          result: { kind: 'error', message: apiErrorMessage(error, 'Search failed.') },
        };
      }
    }

    case 'sites': {
      const { filters } = parsed;
      try {
        const response = await websiteRepository.searchWebsites(
          filters.page,
          filters.domain,
          filters.owner,
          filters.server,
          filters.status,
          filters.limit,
        );
        const websites = response.Websites || [];
        const count = response.count || 0;

        if (!websites.length) {
          return {
            result: {
              kind: 'empty',
              message: `No websites matched /sites ${sitesFiltersToLabel(filters)}.`,
            },
          };
        }

        return {
          result: {
            kind: 'search',
            websites,
            count,
            page: filters.page,
            pageSize: filters.limit,
            filters,
            queryLabel: sitesFiltersToLabel(filters),
          },
        };
      } catch (error) {
        return {
          result: { kind: 'error', message: apiErrorMessage(error, 'Search failed.') },
        };
      }
    }

    case 'use': {
      if (parsed.clear) {
        return {
          result: { kind: 'text', message: 'Website context cleared.' },
          setContext: null,
        };
      }
      if (!parsed.query) {
        if (!deps.context) {
          return {
            result: {
              kind: 'empty',
              message: 'No website context set. Use /use <domain-or-id>.',
            },
          };
        }
        try {
          const composite = await websiteRepository.getWebsiteById(deps.context.id);
          return {
            result: { kind: 'detail', composite },
            setContext: rememberResolved(composite.website),
          };
        } catch (error) {
          if (isNotFound(error)) {
            return {
              result: {
                kind: 'empty',
                message: `Context site #${deps.context.id} no longer exists. Use /use clear.`,
              },
              setContext: null,
            };
          }
          return {
            result: { kind: 'error', message: apiErrorMessage(error, 'Failed to load context site.') },
          };
        }
      }

      const resolved = await resolveWebsiteChoices(websiteRepository, parsed.query);
      if (resolved.error) {
        return { result: { kind: 'error', message: resolved.error } };
      }
      if (resolved.exact) {
        return {
          result: { kind: 'detail', composite: resolved.exact },
          setContext: rememberResolved(resolved.exact.website),
        };
      }
      if (!resolved.websites.length) {
        return {
          result: { kind: 'empty', message: `No website matched "${parsed.query}".` },
        };
      }
      return choicesOutcome(resolved.websites, resolved.count, parsed.query, 'use', ' to use');
    }

    case 'actions': {
      if (!deps.actionDeps) {
        return {
          result: {
            kind: 'error',
            message: 'Action executor is not available in this session.',
          },
        };
      }
      return openConsoleAction('actions.list', deps.actionDeps, parsed.query);
    }
    case 'action': {
      if (!deps.actionDeps) {
        return {
          result: {
            kind: 'error',
            message: 'Action executor is not available in this session.',
          },
        };
      }
      return openConsoleAction(
        parsed.actionId,
        deps.actionDeps,
        parsed.query,
        parsed.args,
      );
    }

    case 'site':
    case 'activity':
    case 'open':
    case 'health':
    case 'dns':
    case 'ssl': {
      const target = requireTarget(
        parsed.query,
        deps.context ? deps.context.id : undefined,
        parsed.kind,
      );
      if ('error' in target) {
        return { result: { kind: 'error', message: target.error } };
      }

      const resolved = await resolveWebsiteChoices(websiteRepository, target.query);
      if (resolved.error) {
        return { result: { kind: 'error', message: resolved.error } };
      }
      if (!resolved.exact) {
        if (!resolved.websites.length) {
          return {
            result: { kind: 'empty', message: `No website matched "${target.query}".` },
          };
        }
        return choicesOutcome(
          resolved.websites,
          resolved.count,
          target.query,
          parsed.kind,
          '',
        );
      }

      const composite = resolved.exact;
      const website = composite.website;
      const ctx = rememberResolved(website);

      if (parsed.kind === 'site') {
        return { result: { kind: 'detail', composite }, setContext: ctx };
      }

      if (parsed.kind === 'open') {
        return openNavigateOutcome(composite, parsed.target, ctx);
      }

      if (parsed.kind === 'activity') {
        try {
          const resources = await websiteRepository.getWebsiteOrchestratorResources(website.id);
          return {
            result: { kind: 'activity', website, resources },
            setContext: ctx,
          };
        } catch (error) {
          return {
            result: { kind: 'error', message: apiErrorMessage(error, 'Failed to load activity.') },
            setContext: ctx,
          };
        }
      }

      if (parsed.kind === 'dns') {
        const dnsResult = await loadDns(deps, website.domain);
        if (!dnsResult.dns) {
          return {
            result: {
              kind: 'error',
              message: dnsResult.error || 'DNS check returned no data.',
            },
            setContext: ctx,
          };
        }
        return {
          result: { kind: 'dns', website, dns: dnsResult.dns },
          setContext: ctx,
        };
      }

      if (parsed.kind === 'ssl') {
        const sslResult = await loadSsl(deps, composite);
        if (!sslResult.certificate) {
          return {
            result: {
              kind: sslResult.error ? 'error' : 'empty',
              message: sslResult.error || `No SSL certificate found for ${website.domain}.`,
            },
            setContext: ctx,
          };
        }
        return {
          result: { kind: 'ssl', website, certificate: sslResult.certificate },
          setContext: ctx,
        };
      }

      // health
      const [dnsResult, sslResult, orch] = await Promise.all([
        loadDns(deps, website.domain),
        loadSsl(deps, composite),
        websiteRepository.getWebsiteOrchestratorResources(website.id).then(
          (resources) => ({ resources }),
          (error) => ({ error: apiErrorMessage(error, 'Failed to load orchestrator.') }),
        ),
      ]);

      let creationStatus: string | undefined;
      let deletionStatus: string | undefined;
      let orchestratorError: string | undefined;

      if ('error' in orch && orch.error) {
        orchestratorError = orch.error;
      } else if ('resources' in orch && orch.resources) {
        const creations = orch.resources.creations || [];
        const deletions = orch.resources.deletions || [];
        if (creations.length) {
          const latest = creations.reduce((a, b) => (a.id > b.id ? a : b));
          creationStatus = latest.status;
        }
        if (deletions.length) {
          const latest = deletions.reduce((a, b) => (a.id > b.id ? a : b));
          deletionStatus = latest.status;
        }
      }

      return {
        result: {
          kind: 'health',
          website,
          composite,
          dns: dnsResult.dns || null,
          dnsError: dnsResult.error,
          ssl: sslResult.certificate || null,
          sslError: sslResult.error,
          creationStatus,
          deletionStatus,
          orchestratorError,
        },
        setContext: ctx,
      };
    }

    case 'logs': {
      const target = requireTarget(
        parsed.query,
        deps.context ? deps.context.id : undefined,
        'logs',
      );
      if ('error' in target) {
        return { result: { kind: 'error', message: target.error } };
      }

      const resolved = await resolveWebsiteChoices(websiteRepository, target.query);
      if (resolved.error) {
        return { result: { kind: 'error', message: resolved.error } };
      }
      if (!resolved.exact) {
        if (!resolved.websites.length) {
          return {
            result: { kind: 'empty', message: `No website matched "${target.query}".` },
          };
        }
        return choicesOutcome(resolved.websites, resolved.count, target.query, 'logs', ' for logs');
      }

      const website = resolved.exact.website;
      const ctx = rememberResolved(website);
      const userId = deps.getUserId();

      try {
        await deps.websiteLogRepository.requestLogs(
          website.id,
          parsed.logType,
          parsed.lines,
          userId,
        );
      } catch (error) {
        return {
          result: { kind: 'error', message: apiErrorMessage(error, 'Failed to request logs.') },
          setContext: ctx,
        };
      }

      return {
        result: {
          kind: 'logs',
          website,
          logType: parsed.logType,
          lines: parsed.lines,
          logs: null,
          pending: true,
        },
        setContext: ctx,
        awaitLogsWebsiteId: website.id,
      };
    }

    default:
      return { result: { kind: 'error', message: 'Unhandled command.' } };
  }
}

export async function loadMoreSearch(
  repository: WebsiteRepositoryInterface,
  previous: Extract<ConsoleResult, { kind: 'search' }>,
): Promise<ConsoleResult> {
  const nextPage = previous.page + 1;
  const filters: SitesFilters = {
    ...previous.filters,
    page: nextPage,
  };

  try {
    const response = await repository.searchWebsites(
      filters.page,
      filters.domain,
      filters.owner,
      filters.server,
      filters.status,
      filters.limit,
    );

    const websites = response.Websites || [];
    const count = response.count || 0;

    if (!websites.length) {
      return {
        kind: 'empty',
        message: 'No more results.',
      };
    }

    return {
      kind: 'search',
      websites: previous.websites.concat(websites),
      count,
      page: nextPage,
      pageSize: filters.limit,
      filters,
      queryLabel: sitesFiltersToLabel(filters),
    };
  } catch (error) {
    return {
      kind: 'error',
      message: apiErrorMessage(error, 'Failed to load more results.'),
    };
  }
}
